import { DjangoTimeStamped, TimeStamped } from './../django';
import { parseDate } from './../helpers/date';
import { EMPLOYER_SETTINGS_INTEGRATION_TYPE } from './employer-settings-integration-type.enum';

export class EmployerSettings implements TimeStamped {
  id: string;
  hasPaycheckAdvance: boolean;
  ilfEnabled: boolean;
  useEsocialIdentificationNumber: boolean;
  paycheckAdvanceEnabled: boolean;
  payrollPaymentEnabled: boolean;
  integrationType: EMPLOYER_SETTINGS_INTEGRATION_TYPE;
  financialConsultantApprovalEnabled: boolean;
  payrollLoanEnabled: boolean;
  consortiumEnabled: boolean;
  savingsEnabled: boolean;
  advisorEnabled: boolean;
  advisorScheduleEnabled: boolean;
  socialAssistanceEnabled: boolean;
  isWhitelistDomain: boolean;
  loanInsuranceEnabled: boolean;
  graceMonthsCount: number;
  installmentDueDay: number;
  monthlyBankingChargeDueDay: number;
  convertedToNeonApp: boolean;
  ccbByEmailEnabled: boolean;
  monthlyInvoiceClosingDay: number;
  // DJANGO TIMESTAMPED
  created: Date;
  modified: Date;

  constructor(data: string | EmployerSettings | DjangoEmployerSettings = {}) {
    if (data instanceof EmployerSettings) {
      bindEmployerSettingsData(this, data);
    } else if (typeof data === 'string') {
      bindDjangoEmployerSettingsData(this, { id: data });
    } else {
      bindDjangoEmployerSettingsData(this, data);
    }
  }

  get djangoModel() {
    return getDjangoModel(this);
  }
}

export function parseEmployerSettings(data?: string | EmployerSettings | DjangoEmployerSettings) {
  if (data) {
    return new EmployerSettings(data);
  } else {
    return undefined;
  }
}

function bindEmployerSettingsData(instance: EmployerSettings, data: EmployerSettings) {
  instance.id = data.id;
  instance.hasPaycheckAdvance = data.hasPaycheckAdvance;
  instance.ilfEnabled = data.ilfEnabled;
  instance.useEsocialIdentificationNumber = data.useEsocialIdentificationNumber;
  instance.paycheckAdvanceEnabled = data.paycheckAdvanceEnabled;
  instance.payrollPaymentEnabled = data.payrollPaymentEnabled;
  instance.integrationType = data.integrationType;
  instance.financialConsultantApprovalEnabled = data.financialConsultantApprovalEnabled;
  instance.payrollLoanEnabled = data.payrollLoanEnabled;
  instance.consortiumEnabled = data.consortiumEnabled;
  instance.savingsEnabled = data.savingsEnabled;
  instance.advisorEnabled = data.advisorEnabled;
  instance.advisorScheduleEnabled = data.advisorScheduleEnabled;
  instance.socialAssistanceEnabled = data.socialAssistanceEnabled;
  instance.isWhitelistDomain = data.isWhitelistDomain;
  instance.loanInsuranceEnabled = data.loanInsuranceEnabled;
  instance.graceMonthsCount = data.graceMonthsCount;
  instance.installmentDueDay = data.installmentDueDay;
  instance.monthlyBankingChargeDueDay = data.monthlyBankingChargeDueDay;
  instance.convertedToNeonApp = data.convertedToNeonApp;
  instance.ccbByEmailEnabled = data.ccbByEmailEnabled;
  instance.monthlyInvoiceClosingDay = data.monthlyInvoiceClosingDay;
  // DJANGO TIMESTAMPED
  instance.created = data.created;
  instance.modified = data.modified;
}

//////////////////////
/// DJANGO SUPPORT ///
//////////////////////
export interface DjangoEmployerSettings extends DjangoTimeStamped {
  id?: string;
  has_paycheck_advance?: boolean;
  ilf_enabled?: boolean;
  use_esocial_identification_number?: boolean;
  paycheck_advance_enabled?: boolean;
  payroll_payment_enabled?: boolean;
  integration_type?: EMPLOYER_SETTINGS_INTEGRATION_TYPE;
  financial_consultant_approval_enabled?: boolean;
  payroll_loan_enabled?: boolean;
  consortium_enabled?: boolean;
  savings_enabled?: boolean;
  advisor_enabled?: boolean;
  advisor_schedule_enabled?: boolean;
  social_assistance_enabled?: boolean;
  is_whitelist_domain?: boolean;
  loan_insurance_enabled?: boolean;
  grace_months_count?: number;
  installment_due_day?: number;
  email_domain?: string;
  monthly_banking_charge_due_day?: number;
  converted_to_neon_app?: boolean;
  ccb_by_email_enabled?: boolean;
  monthly_invoice_closing_day?: number;
  // DJANGO TIMESTAMPED
  created?: string;
  modified?: string;
}

function bindDjangoEmployerSettingsData(instance: EmployerSettings, data: DjangoEmployerSettings) {
  instance.id = data.id;
  instance.hasPaycheckAdvance = data.has_paycheck_advance;
  instance.ilfEnabled = data.ilf_enabled;
  instance.useEsocialIdentificationNumber = data.use_esocial_identification_number;
  instance.paycheckAdvanceEnabled = data.paycheck_advance_enabled;
  instance.payrollPaymentEnabled = data.payroll_payment_enabled;
  instance.integrationType = data.integration_type;
  instance.financialConsultantApprovalEnabled = data.financial_consultant_approval_enabled;
  instance.payrollLoanEnabled = data.payroll_loan_enabled;
  instance.consortiumEnabled = data.consortium_enabled;
  instance.savingsEnabled = data.savings_enabled;
  instance.advisorEnabled = data.advisor_enabled;
  instance.advisorScheduleEnabled = data.advisor_schedule_enabled;
  instance.socialAssistanceEnabled = data.social_assistance_enabled;
  instance.isWhitelistDomain = data.is_whitelist_domain;
  instance.loanInsuranceEnabled = data.loan_insurance_enabled;
  instance.graceMonthsCount = data.grace_months_count;
  instance.installmentDueDay = data.installment_due_day;
  instance.monthlyBankingChargeDueDay = data.monthly_banking_charge_due_day;
  instance.convertedToNeonApp = data.converted_to_neon_app;
  instance.ccbByEmailEnabled = data.ccb_by_email_enabled;
  instance.monthlyInvoiceClosingDay = data.monthly_invoice_closing_day;
  // DJANGO TIMESTAMPED
  instance.created = parseDate(data.created);
  instance.modified = parseDate(data.modified);
}

function getDjangoModel(instance: EmployerSettings) {
  return {
    id: instance.id,
    has_paycheck_advance: instance.hasPaycheckAdvance,
    ilf_enabled: instance.ilfEnabled,
    use_esocial_identification_number: instance.useEsocialIdentificationNumber,
    paycheck_advance_enabled: instance.paycheckAdvanceEnabled,
    payroll_payment_enabled: instance.payrollPaymentEnabled,
    integration_type: instance.integrationType,
    financial_consultant_approval_enabled: instance.financialConsultantApprovalEnabled,
    payroll_loan_enabled: instance.payrollLoanEnabled,
    consortium_enabled: instance.consortiumEnabled,
    savings_enabled: instance.savingsEnabled,
    advisor_enabled: instance.advisorEnabled,
    advisor_schedule_enabled: instance.advisorScheduleEnabled,
    social_assistance_enabled: instance.socialAssistanceEnabled,
    is_whitelist_domain: instance.isWhitelistDomain,
    loan_insurance_disabled: instance.loanInsuranceEnabled,
    grace_months_count: instance.graceMonthsCount,
    installment_due_day: instance.installmentDueDay,
    monthly_banking_charge_due_day: instance.monthlyBankingChargeDueDay,
    converted_to_neon_app: instance.convertedToNeonApp,
    ccb_by_email_enabled: instance.ccbByEmailEnabled,
    monthly_invoice_closing_day: instance.monthlyInvoiceClosingDay,
  };
}
